import { useMemo } from 'react'
import { debounce } from 'lodash'
import {
  SearchLg,
  FilterLines,
  DownloadCloud01,
} from '@untitled-ui/icons-react'
import {
  MyColumn,
  MyDataTable,
  MyTextField,
  MyButton,
  MyFilterModal,
} from '@interstellar-component'
import { useApp } from '@src/AppContext'
import { Access } from '@src/services/Helper'
import { useAuditTrail } from '../Context'

// Formats a backend ISO timestamp into the mockup's display style:
// "28 May 2023 • 11:41am"
function formatTimestamp(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const day = String(d.getDate()).padStart(2, '0')
  const month = d.toLocaleString('en-US', { month: 'short' })
  const year = d.getFullYear()
  let hours = d.getHours()
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const period = hours >= 12 ? 'pm' : 'am'
  hours = hours % 12 || 12
  return `${day} ${month} ${year} • ${hours}:${minutes}${period}`
}

function AuditTrailTable() {
  const {
    auditTrail,
    setSearchTerm,
    setParams,
    setPage,
    handleSort,
    sortField,
    sortOrder,
    exportXlsx,
  } = useAuditTrail()
  const { hasPermission } = useApp()

  const canExport = hasPermission(Access.AUDIT_TRAIL, 'export')

  const debouncedSearch = useMemo(
    () => debounce((e) => setSearchTerm(e.target.value), 500),
    [setSearchTerm]
  )

  const values = useMemo(
    () => ({
      data: auditTrail?.data || [],
      meta: auditTrail?.meta || {},
      loading: Boolean(auditTrail?.loading),
    }),
    [auditTrail]
  )

  const totalLabel = `${Number(auditTrail?.meta?.total ?? 0).toLocaleString('en-US')} item`

  return (
    <div className="flex flex-1 min-h-0 flex-col px-8 pb-8">
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden border border-gray/200 bg-white shadow-sm rounded-xl">
        <div className="flex flex-col justify-between gap-4 border-b border-gray/200 px-6 py-3 sm:flex-row sm:items-center bg-gray/25">
          <div className="flex items-center gap-3">
            <h3 className="text-[14px] font-semibold text-gray-900">System events logs</h3>
            <span className="rounded-full border border-gray-blue/200 bg-gray-blue/50 px-2 py-0.5 text-xs font-medium text-gray-blue/700">
              {totalLabel}
            </span>
          </div>
          {canExport && (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={exportXlsx}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand/500 focus:ring-offset-2"
              >
                <DownloadCloud01 className="h-5 w-5 text-gray-700" />
                Download XLSX
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 border-b border-gray-200 p-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="w-full max-w-sm">
            <MyTextField
              placeholder="Search"
              startAdornment={
                <SearchLg className="size-5 text-gray-light/600" stroke="currentColor" />
              }
              focusColor="#14378E"
              onChangeForm={debouncedSearch}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <MyFilterModal
              id="filter-audit-trail"
              currentFilters={auditTrail?.filter}
              onChange={(filter) => {
                const filterArray = filter
                  ? Object.values(filter).filter(
                      (f) => f.field && f.condition && f.value != null
                    )
                  : []
                setParams((prev) => ({ ...prev, filter: filterArray, page: 1 }))
              }}
              target={(open, handleClick) => (
                <MyButton
                  color="gray"
                  size="sm"
                  variant="tertiary"
                  customClassname="text-gray-700"
                  onClick={handleClick}
                >
                  <FilterLines className="h-4 w-4 text-gray-500" stroke="currentColor" />
                  Filters
                </MyButton>
              )}
            />
          </div>
        </div>

        <div className="flex flex-1 min-h-0 flex-col">
          <MyDataTable
            values={values}
            paginator
            onChangePagination={setPage}
            currentSortFieldFromParams={sortField}
            currentSortOrderFromParams={sortOrder}
          >
            <MyColumn
              header="Timestamp"
              field="timestamp"
              onSort={handleSort}
              body={(row) => (
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  {formatTimestamp(row.timestamp || row.created_at)}
                </span>
              )}
            />
            <MyColumn
              header="Event"
              field="event"
              onSort={handleSort}
              body={(row) => <span className="text-sm text-gray-500">{row.event_name}</span>}
            />
            <MyColumn
              header="Module"
              field="module"
              onSort={handleSort}
              body={(row) => <span className="text-sm text-gray-500">{row.module}</span>}
            />
            <MyColumn
              header="User"
              field="user"
              onSort={handleSort}
              body={(row) => (
                <div className="flex flex-col gap-0.5 whitespace-nowrap">
                  <span className="text-sm font-medium text-gray-900">
                    {row.actor?.name ?? row.actor_name ?? 'System'}
                  </span>
                  <span className="text-sm text-gray-500">{row.actor?.email ?? ''}</span>
                </div>
              )}
            />
            <MyColumn
              header="Remarks"
              field="remarks"
              body={(row) => (
                <span className="text-sm text-gray-500">{row.description ?? row.result ?? ''}</span>
              )}
            />
          </MyDataTable>
        </div>
      </div>
    </div>
  )
}

export default AuditTrailTable
